import { Alert, Box, Button, paletteColors, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import RefreshIcon from '@evaneos/b2b-ui/icons/Refresh';

import { PENDING, ERROR } from '../../../../../design-system/constants';
import { Loader } from '../../../../../design-system/Loader';
import {
    useAllDiscussionMessages,
    useGetDiscussionAttachments,
} from '../../../../../resources/discussion/queries';
import { DossierId } from '../../../../../resources/dossier/types';
import { categorizeAttachments } from '../../../../../resources/discussion/operations';

import { TipiSection } from './TipiSection';
import { PhotosSection } from './PhotosSection';
import { FilesSection } from './FilesSection';

type AttachmentTabsProps = {
    dossierId: DossierId;
    isActive: boolean;
};

export const AttachmentsTab = ({ dossierId, isActive }: AttachmentTabsProps) => {
    const { formatMessage } = useIntl();

    const {
        data: attachments = [],
        status: attachmentsStatus,
        refetch,
    } = useGetDiscussionAttachments(dossierId, isActive);

    const { data: messagesData } = useAllDiscussionMessages(dossierId);
    const messagesMap = new Map(
        (messagesData?.messages ?? []).map((m) => [
            Number(m.id),
            { authorName: m.authorName, authorId: m.authorId, sendDate: m.sendDate },
        ]),
    );

    const getMessageInfo = (messageId?: number | null) => {
        if (messageId == null) return undefined;
        return messagesMap.get(messageId);
    };

    const { tipiLinks, images, files } = categorizeAttachments(attachments);
    const totalNumberOfDocuments = images.length + files.length;

    if (attachmentsStatus === PENDING) {
        return (
            <Box
                data-spec-id="attachments-panel"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    gap: 1,
                }}
            >
                <Loader color={paletteColors.green[700]} />
                <Typography>
                    {formatMessage({ id: 'discussion.attachments_list.loading' })}
                </Typography>
            </Box>
        );
    }

    if (attachmentsStatus === ERROR) {
        return (
            <Box sx={{ flex: 1, p: 3 }}>
                <Alert severity="error" data-spec-id="attachments-error">
                    <Typography
                        variant="body2"
                        sx={{ color: 'inherit', fontWeight: 'bold', marginBottom: 0.5 }}
                    >
                        {formatMessage({ id: 'discussion.attachments_list.error_title' })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'inherit', marginBottom: 1.5 }}>
                        {formatMessage({ id: 'discussion.attachments_list.error_description' })}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => void refetch()}
                    >
                        {formatMessage({ id: 'global.retry' })}
                    </Button>
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            data-spec-id="attachments-panel"
            data-testid="attachments-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                overflowY: 'auto',
                p: 3,
                gap: 1,
            }}
        >
            <Typography variant="h4" component="h3">
                {formatMessage(
                    { id: 'discussion.attachments_list.tipi_proposals_section_title' },
                    { count: tipiLinks.length },
                )}
            </Typography>
            <TipiSection tipiLinks={tipiLinks} />

            <Typography variant="h4" component="h3" mt={4}>
                {formatMessage(
                    { id: 'discussion.attachments_list.documents_section_title' },
                    { count: totalNumberOfDocuments },
                )}
            </Typography>
            {totalNumberOfDocuments === 0 ? (
                <Typography color="text.secondary" variant="body2">
                    {formatMessage({ id: 'discussion.attachments_list.documents_empty' })}
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <PhotosSection photos={images} getMessageInfo={getMessageInfo} />
                    <FilesSection files={files} getMessageInfo={getMessageInfo} />
                </Box>
            )}
        </Box>
    );
};
